'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Building,
  Building2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { OrgTreeNode } from '@/api/organizations';
import { CreateOrganizationDialog } from '@/components/organizations/create-organization-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useCreateDepartment,
  useDeleteDepartment,
  useDepartments,
  useUpdateDepartment,
} from '@/features/departments/api/use-departments';
import { DepartmentForm } from '@/features/departments/components/DepartmentForm';
import type { DepartmentCreateInput, DepartmentListItem } from '@/features/departments/types';
import {
  useCreatePlatformFacility,
  useDeletePlatformFacility,
  usePlatformFacilities,
  useUpdatePlatformFacility,
} from '@/features/facilities/api/use-facilities';
import { FacilityForm } from '@/features/facilities/components/FacilityForm';
import type {
  PlatformFacilityCreateInput,
  PlatformFacilityListItem,
} from '@/features/facilities/types';
import {
  DataVisibilityCard,
  OrganizationSettingsCard,
  OrganizationSettingsSkeleton,
  SecuritySettingsCard,
  TaskPoliciesCard,
  useOrganizationSettings,
  useTaskPolicies,
} from '@/features/settings/organization';
import {
  CreateSubsidiaryDialog,
} from '@/features/settings/subsidiaries';
import { useDeleteSubsidiary } from '@/features/settings/subsidiaries/api/use-delete-subsidiary';
import { useOrgForest, useOrgTree } from '@/features/settings/subsidiaries/api/use-org-tree';
import { EditSubsidiaryDialog } from '@/features/settings/subsidiaries/components/edit-subsidiary-dialog';
import { InheritedMembersCard } from '@/features/settings/subsidiaries/components/inherited-members-card';
import { MoveSubsidiaryDialog } from '@/features/settings/subsidiaries/components/move-subsidiary-dialog';
import { OrgForestCard, OrgTreeCard } from '@/features/settings/subsidiaries/components/org-tree';
import { useOrgRole } from '@/hooks/use-org-role';
import { useOrganizationId } from '@/hooks/use-organization-id';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/settings/organisation-structure')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.organisationStructure'),
  }),
  component: OrganisationStructurePage,
});

function OrganisationStructurePage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { isAdmin, isLoading: isLoadingRole } = useOrgRole();

  useEffect(() => {
    if (!isLoadingRole && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isLoadingRole, isAdmin, navigate]);

  if (!isLoadingRole && !isAdmin) {
    return null;
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('organisationStructure.pageTitle')}</h1>
          <p className="mt-1 text-muted-foreground">
            {t('organisationStructure.pageDescription')}
          </p>
        </header>

        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">{t('organisationStructure.tabOverview')}</TabsTrigger>
            <TabsTrigger value="departments">{t('organisationStructure.tabDepartments')}</TabsTrigger>
            <TabsTrigger value="facilities">{t('organisationStructure.tabFacilities')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewTab />
          </TabsContent>
          <TabsContent value="departments" className="mt-6">
            <DepartmentsTab />
          </TabsContent>
          <TabsContent value="facilities" className="mt-6">
            <FacilitiesTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* ─── Overview Tab ─── */

function OverviewTab() {
  const { t } = useTranslation('settings');
  const { isAdmin } = useOrgRole();
  const { data: settings, isLoading, error, refetch } = useOrganizationSettings();
  const { data: taskPolicies, isLoading: isLoadingTaskPolicies } = useTaskPolicies();
  const [isSubsidiaryDialogOpen, setIsSubsidiaryDialogOpen] = useState(false);
  const [isPeerOrgDialogOpen, setIsPeerOrgDialogOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<OrgTreeNode | null>(null);
  const [createParentId, setCreateParentId] = useState<string | undefined>(undefined);
  const [editNode, setEditNode] = useState<OrgTreeNode | null>(null);
  const [deleteNode, setDeleteNode] = useState<OrgTreeNode | null>(null);
  const [moveNode, setMoveNode] = useState<OrgTreeNode | null>(null);

  const deleteSubsidiary = useDeleteSubsidiary();
  const orgId = useOrganizationId();

  const {
    data: treeData,
    isLoading: isLoadingTree,
    error: treeError,
    refetch: refetchTree,
  } = useOrgTree(orgId);

  const {
    data: forestData,
    isLoading: isLoadingForest,
    error: forestError,
    refetch: refetchForest,
  } = useOrgForest();

  // Render the forest when the user administers more than one top-level org.
  // Otherwise keep the single-tree experience to avoid UI churn for the
  // common one-root case.
  const showForest = (forestData?.trees.length ?? 0) > 1;

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-center">
        <p className="text-sm text-destructive mb-4">
          {error instanceof Error ? error.message : t('organisationStructure.failedToLoad')}
        </p>
        <Button variant="outline" onClick={() => void refetch()}>
          {t('organisationStructure.tryAgain')}
        </Button>
      </div>
    );
  }

  if (isLoading || isLoadingTaskPolicies || !settings) {
    return <OrganizationSettingsSkeleton />;
  }

  const handleCreateChild = (parentId: string) => {
    setCreateParentId(parentId);
    setIsSubsidiaryDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteNode || !orgId) return;
    try {
      await deleteSubsidiary.mutateAsync({
        parentOrgId: orgId,
        subsidiaryId: deleteNode.id,
      });
      toast.success(t('organisationStructure.subsidiaryDeleted', { name: deleteNode.name }));
      setDeleteNode(null);
      void refetchTree();
      void refetchForest();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('organisationStructure.failedToDeleteSubsidiary');
      toast.error(message);
    }
  };

  return (
    <div className="space-y-6">
      {orgId && (
        <div className="space-y-4">
          {showForest ? (
            <OrgForestCard
              forest={forestData?.trees}
              activeRootId={orgId}
              isLoading={isLoadingForest}
              error={forestError}
              onRetry={() => void refetchForest()}
              onCreateChild={handleCreateChild}
              onCreateSibling={handleCreateChild}
              onSelectNode={(node) => { setSelectedNode(node); }}
              onEditNode={(node) => { setEditNode(node); }}
              onDeleteNode={(node) => { setDeleteNode(node); }}
              onMoveNode={(node) => { setMoveNode(node); }}
              onCreateSubsidiaryClick={() => { setIsSubsidiaryDialogOpen(true); }}
              onCreatePeerClick={() => { setIsPeerOrgDialogOpen(true); }}
              isAdmin={isAdmin}
            />
          ) : (
            <OrgTreeCard
              tree={treeData?.tree}
              isLoading={isLoadingTree}
              error={treeError}
              onRetry={() => void refetchTree()}
              onCreateChild={handleCreateChild}
              onCreateSibling={handleCreateChild}
              onSelectNode={(node) => { setSelectedNode(node); }}
              onEditNode={(node) => { setEditNode(node); }}
              onDeleteNode={(node) => { setDeleteNode(node); }}
              onMoveNode={(node) => { setMoveNode(node); }}
              onCreateClick={() => { setIsSubsidiaryDialogOpen(true); }}
              onCreatePeerClick={() => { setIsPeerOrgDialogOpen(true); }}
              isAdmin={isAdmin}
              totalNodes={treeData?.totalNodes}
            />
          )}

          {selectedNode && selectedNode.level > 0 && (
            <InheritedMembersCard
              organizationId={selectedNode.id}
              organizationName={selectedNode.name}
            />
          )}

          <CreateSubsidiaryDialog
            parentOrganization={settings}
            parentOrgId={createParentId}
            open={isSubsidiaryDialogOpen}
            onOpenChange={(open) => {
              setIsSubsidiaryDialogOpen(open);
              if (!open) {
                setCreateParentId(undefined);
                void refetchTree();
                void refetchForest();
              }
            }}
          />

          <CreateOrganizationDialog
            open={isPeerOrgDialogOpen}
            onOpenChange={(open) => {
              setIsPeerOrgDialogOpen(open);
              if (!open) {
                void refetchForest();
              }
            }}
          />

          {/* Edit Subsidiary Dialog */}
          {orgId && (
            <EditSubsidiaryDialog
              node={editNode}
              parentOrgId={orgId}
              open={Boolean(editNode)}
              onOpenChange={(open) => { if (!open) setEditNode(null); }}
              onSuccess={() => {
                toast.success(t('organisationStructure.subsidiaryUpdated', { name: editNode?.name }));
                setEditNode(null);
                void refetchTree();
                void refetchForest();
              }}
            />
          )}

          {/* Move / Attach Organisation Dialog */}
          {orgId && (() => {
            const roots: OrgTreeNode[] =
              forestData?.trees.map((entry) => entry.tree) ??
              (treeData?.tree ? [treeData.tree] : []);
            const isPeerAttach =
              moveNode?.parentOrganizationId == null;
            return (
              <MoveSubsidiaryDialog
                node={moveNode}
                roots={roots}
                open={Boolean(moveNode)}
                onOpenChange={(open) => { if (!open) setMoveNode(null); }}
                onSuccess={() => {
                  toast.success(
                    isPeerAttach
                      ? t('organisationStructure.organisationAttached', {
                          defaultValue: '{{name}} attached to new parent',
                          name: moveNode?.name,
                        })
                      : t('organisationStructure.subsidiaryMoved', { name: moveNode.name }),
                  );
                  setMoveNode(null);
                  void refetchTree();
                  void refetchForest();
                }}
              />
            );
          })()}

          {/* Delete Subsidiary Confirmation */}
          <AlertDialog open={Boolean(deleteNode)} onOpenChange={(open) => { if (!open) setDeleteNode(null); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('organisationStructure.deleteSubsidiary')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('organisationStructure.deleteSubsidiaryConfirm', { name: deleteNode?.name })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('organisationStructure.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => void handleDeleteConfirm()}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={deleteSubsidiary.isPending}
                >
                  {deleteSubsidiary.isPending ? t('organisationStructure.deleting') : t('organisationStructure.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <div className="max-w-2xl space-y-6">
        <OrganizationSettingsCard organization={settings} />
        <SecuritySettingsCard securitySettings={settings.securitySettings} isAdmin={isAdmin} />
        <DataVisibilityCard
          dataVisibilitySettings={settings.dataVisibilitySettings}
          hasParent={settings.parentOrganization !== null}
        />
        {taskPolicies && (
          <TaskPoliciesCard
            taskPolicies={taskPolicies}
            hasParent={settings.parentOrganization !== null}
            isAdmin={isAdmin}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Departments Tab ─── */

function DepartmentsTab() {
  const { t } = useTranslation('settings');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DepartmentListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DepartmentListItem | null>(null);

  const params: Record<string, string> = {
    page: String(page),
    page_size: String(pageSize),
  };
  if (search) params.search = search;

  const { data, isLoading } = useDepartments(params);
  const { data: allDepts } = useDepartments({ page_size: '200' });
  const { data: facilitiesData } = usePlatformFacilities({ page_size: '200' });

  const createMutation = useCreateDepartment();
  const updateMutation = useUpdateDepartment();
  const deleteMutation = useDeleteDepartment();

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const handleCreate = async (formData: DepartmentCreateInput) => {
    try {
      await createMutation.mutateAsync(formData);
      toast.success(t('settings:toast.departmentCreated'));
      setFormOpen(false);
    } catch {
      toast.error(t('settings:toast.failedToCreateDepartment'));
    }
  };

  const handleEdit = async (formData: DepartmentCreateInput) => {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ departmentId: editTarget.id, data: formData });
      toast.success(t('settings:toast.departmentUpdated'));
      setEditTarget(null);
    } catch {
      toast.error(t('settings:toast.failedToUpdateDepartment'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t('settings:toast.deleted', { deleteTarget: deleteTarget.name }));
      setDeleteTarget(null);
    } catch {
      toast.error(t('settings:toast.failedToDeleteDepartment'));
    }
  };

  const departments = allDepts?.items ?? [];
  const facilities = (facilitiesData?.items ?? []).map((f) => ({
    id: f.id,
    name: f.name,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('organisationStructure.manageDepartments')}
        </p>
        <Button
          onClick={() => {
            setEditTarget(null);
            setFormOpen(true);
          }}
          data-testid="create-department-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('organisationStructure.createDepartment')}
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('organisationStructure.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-9"
            data-testid="department-search"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : !data?.items.length ? (
        <EmptyState
          icon={Building}
          title={t('organisationStructure.noDepartmentsFound')}
          description={
            search
              ? t('organisationStructure.tryAdjustingSearch')
              : t('organisationStructure.createFirstDepartment')
          }
          {...(!search
            ? {
                action: {
                  label: t('organisationStructure.createDepartment'),
                  onClick: () => {
                    setEditTarget(null);
                    setFormOpen(true);
                  },
                },
              }
            : {})}
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('organisationStructure.columnName')}</TableHead>
                    <TableHead>{t('organisationStructure.columnCode')}</TableHead>
                    <TableHead>{t('organisationStructure.columnParent')}</TableHead>
                    <TableHead>{t('organisationStructure.columnFacility')}</TableHead>
                    <TableHead>{t('organisationStructure.columnStatus')}</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((dept: DepartmentListItem) => (
                    <TableRow key={dept.id} data-testid={`department-row-${dept.id}`}>
                      <TableCell className="font-medium">{dept.name}</TableCell>
                      <TableCell className="text-muted-foreground">{dept.code}</TableCell>
                      <TableCell>{dept.parentDepartmentName ?? '-'}</TableCell>
                      <TableCell>{dept.facilityName ?? '-'}</TableCell>
                      <TableCell>
                        <Badge variant={dept.isActive ? 'default' : 'secondary'}>
                          {dept.isActive ? t('organisationStructure.active') : t('organisationStructure.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              data-testid={`department-actions-${dept.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditTarget(dept);
                                setFormOpen(true);
                              }}
                              data-testid={`department-edit-${dept.id}`}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('organisationStructure.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => { setDeleteTarget(dept); }}
                              data-testid={`department-delete-${dept.id}`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('organisationStructure.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t('organisationStructure.showingRange', { start: (page - 1) * pageSize + 1, end: Math.min(page * pageSize, data.total), total: data.total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => { setPage((p) => p - 1); }}
                  data-testid="department-prev-page"
                >
                  {t('organisationStructure.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => { setPage((p) => p + 1); }}
                  data-testid="department-next-page"
                >
                  {t('organisationStructure.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create / Edit Dialog */}
      <DepartmentForm
        key={editTarget?.id ?? 'new'}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditTarget(null);
        }}
        onSubmit={(data) => void (editTarget ? handleEdit(data) : handleCreate(data))}
        isPending={createMutation.isPending || updateMutation.isPending}
        departments={departments}
        facilities={facilities}
        editData={editTarget}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={() => { setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('organisationStructure.deleteDepartment')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('organisationStructure.deleteDepartmentConfirm', { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="department-delete-cancel">{t('organisationStructure.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="department-delete-confirm"
            >
              {t('organisationStructure.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Facilities Tab ─── */

function FacilitiesTab() {
  const { t } = useTranslation('settings');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PlatformFacilityListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlatformFacilityListItem | null>(null);

  const params: Record<string, string> = {
    page: String(page),
    page_size: String(pageSize),
  };
  if (search) params.search = search;
  if (statusFilter) params.status = statusFilter;

  const { data, isLoading } = usePlatformFacilities(params);
  const createMutation = useCreatePlatformFacility();
  const updateMutation = useUpdatePlatformFacility();
  const deleteMutation = useDeletePlatformFacility();

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const handleCreate = async (formData: PlatformFacilityCreateInput) => {
    try {
      await createMutation.mutateAsync(formData);
      toast.success(t('settings:toast.facilityCreated'));
      setFormOpen(false);
    } catch {
      toast.error(t('settings:toast.failedToCreateFacility'));
    }
  };

  const handleEdit = async (formData: PlatformFacilityCreateInput) => {
    if (!editTarget) return;
    try {
      await updateMutation.mutateAsync({ facilityId: editTarget.id, data: formData });
      toast.success(t('settings:toast.facilityUpdated'));
      setEditTarget(null);
    } catch {
      toast.error(t('settings:toast.failedToUpdateFacility'));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t('settings:toast.deleted2', { deleteTarget: deleteTarget.name }));
      setDeleteTarget(null);
    } catch {
      toast.error(t('settings:toast.failedToDeleteFacility'));
    }
  };

  const statusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default' as const;
      case 'inactive':
        return 'secondary' as const;
      case 'planned':
        return 'outline' as const;
      case 'decommissioned':
        return 'destructive' as const;
      default:
        return 'secondary' as const;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('organisationStructure.manageFacilities')}
        </p>
        <Button
          onClick={() => {
            setEditTarget(null);
            setFormOpen(true);
          }}
          data-testid="create-facility-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('organisationStructure.createFacility')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('organisationStructure.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-9"
            data-testid="facility-search"
          />
        </div>
        <NativeSelect
          options={[
            { value: '', label: t('organisationStructure.allStatus') },
            { value: 'active', label: t('organisationStructure.active') },
            { value: 'inactive', label: t('organisationStructure.inactive') },
            { value: 'planned', label: t('organisationStructure.planned') },
            { value: 'decommissioned', label: t('organisationStructure.decommissioned') },
          ]}
          value={statusFilter}
          onChange={(val) => {
            setStatusFilter(val);
            setPage(1);
          }}
          className="w-44"
          data-testid="facility-status-filter"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : !data?.items.length ? (
        <EmptyState
          icon={Building2}
          title={t('organisationStructure.noFacilitiesFound')}
          description={
            search || statusFilter
              ? t('organisationStructure.tryAdjustingFilters')
              : t('organisationStructure.createFirstFacility')
          }
          {...(!search && !statusFilter
            ? {
                action: {
                  label: t('organisationStructure.createFacility'),
                  onClick: () => {
                    setEditTarget(null);
                    setFormOpen(true);
                  },
                },
              }
            : {})}
        />
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('organisationStructure.columnName')}</TableHead>
                    <TableHead>{t('organisationStructure.columnCode')}</TableHead>
                    <TableHead>{t('organisationStructure.columnType')}</TableHead>
                    <TableHead>{t('organisationStructure.columnCountry')}</TableHead>
                    <TableHead>{t('organisationStructure.columnStatus')}</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((fac: PlatformFacilityListItem) => (
                    <TableRow key={fac.id} data-testid={`facility-row-${fac.id}`}>
                      <TableCell className="font-medium">{fac.name}</TableCell>
                      <TableCell className="text-muted-foreground">{fac.code}</TableCell>
                      <TableCell className="capitalize">
                        {t(`organisationStructure.facilityTypes.${fac.facilityType}`, { defaultValue: fac.facilityType.replace(/_/g, ' ') })}
                      </TableCell>
                      <TableCell>{fac.country}</TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(fac.status)}>
                          {t(`organisationStructure.${fac.status}`, { defaultValue: fac.status })}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              data-testid={`facility-actions-${fac.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditTarget(fac);
                                setFormOpen(true);
                              }}
                              data-testid={`facility-edit-${fac.id}`}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('organisationStructure.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => { setDeleteTarget(fac); }}
                              data-testid={`facility-delete-${fac.id}`}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('organisationStructure.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t('organisationStructure.showingRange', { start: (page - 1) * pageSize + 1, end: Math.min(page * pageSize, data.total), total: data.total })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => { setPage((p) => p - 1); }}
                  data-testid="facility-prev-page"
                >
                  {t('organisationStructure.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => { setPage((p) => p + 1); }}
                  data-testid="facility-next-page"
                >
                  {t('organisationStructure.next')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create / Edit Dialog */}
      <FacilityForm
        key={editTarget?.id ?? 'new'}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditTarget(null);
        }}
        onSubmit={(data) => void (editTarget ? handleEdit(data) : handleCreate(data))}
        isPending={createMutation.isPending || updateMutation.isPending}
        editData={editTarget}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={() => { setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('organisationStructure.deleteFacility')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('organisationStructure.deleteFacilityConfirm', { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="facility-delete-cancel">{t('organisationStructure.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="facility-delete-confirm"
            >
              {t('organisationStructure.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
